import { useEffect, useMemo, useState } from "react";
import { AlertTriangle, CalendarClock, Download, FileText, Filter, GraduationCap, HardHat, HeartPulse, ListChecks, RefreshCcw } from "lucide-react";
import { apiRequest, downloadFile } from "@/api/client";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";

const sourceMeta = {
  improvement_plan: { label: "Piano miglioramento", icon: ListChecks, color: "bg-blue-50 text-blue-900" },
  training:         { label: "Formazione",          icon: GraduationCap, color: "bg-violet-50 text-violet-900" },
  ppe:              { label: "DPI",                 icon: HardHat,    color: "bg-amber-50 text-amber-900" },
  health_surveillance: { label: "Sorveglianza sanitaria", icon: HeartPulse, color: "bg-rose-50 text-rose-900" },
  document:         { label: "Documento",           icon: FileText,   color: "bg-slate-100 text-slate-800" },
};
const severityMeta = {
  overdue:   { label: "Scaduta",   color: "bg-red-100 text-red-800 border-red-200" },
  urgent:    { label: "Urgente",   color: "bg-orange-100 text-orange-800 border-orange-200" },
  soon:      { label: "A breve",   color: "bg-amber-100 text-amber-800 border-amber-200" },
  scheduled: { label: "Pianificata", color: "bg-emerald-100 text-emerald-800 border-emerald-200" },
};

function StatTile({ label, value, accent, testId }) {
  return (
    <Card className="shadow-none" data-testid={testId}>
      <CardContent className="p-4">
        <p className="text-xs uppercase tracking-wider text-slate-500">{label}</p>
        <p className={`mt-1 font-heading text-3xl font-bold ${accent}`}>{value}</p>
      </CardContent>
    </Card>
  );
}

export default function Deadlines() {
  const [summary, setSummary] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [filterSource, setFilterSource] = useState("");
  const [filterSeverity, setFilterSeverity] = useState("");
  const [search, setSearch] = useState("");

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const qs = new URLSearchParams();
      if (filterSource) qs.set("source", filterSource);
      if (filterSeverity) qs.set("severity", filterSeverity);
      const path = qs.toString() ? `/deadlines?${qs.toString()}` : "/deadlines";
      setSummary(await apiRequest(path));
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, [filterSource, filterSeverity]);

  const filteredItems = useMemo(() => {
    if (!summary) return [];
    if (!search) return summary.items;
    const q = search.toLowerCase();
    return summary.items.filter((i) => (i.title || "").toLowerCase().includes(q) || (i.company_name || "").toLowerCase().includes(q) || (i.description || "").toLowerCase().includes(q));
  }, [summary, search]);

  const exportCsv = async () => {
    try { await downloadFile("/deadlines/export", "scadenze.csv"); }
    catch (err) { setError(err.message); }
  };

  const exportPdf = async () => {
    try { await downloadFile("/deadlines/export-pdf", "scadenzario.pdf"); }
    catch (err) { setError(err.message); }
  };

  if (!summary) {
    return <div className="rounded-lg border border-slate-200 bg-white p-6" data-testid="deadlines-loading">Caricamento scadenze...</div>;
  }

  return (
    <div className="space-y-6" data-testid="deadlines-page">
      <section className="rounded-lg border border-slate-200 bg-white p-6">
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-blue-900">Compliance unificata</p>
        <h2 className="mt-2 font-heading text-4xl font-bold text-slate-950">Scadenze</h2>
        <p className="mt-2 text-slate-600">Tutte le scadenze attive su piano miglioramento, formazione, DPI, sorveglianza sanitaria e documenti aziendali.</p>
        <div className="mt-4 flex flex-wrap gap-2">
          <Button variant="outline" onClick={load} disabled={loading} data-testid="deadlines-refresh-button"><RefreshCcw className="h-4 w-4" /> Aggiorna</Button>
          <Button className="bg-blue-900 hover:bg-blue-800" onClick={exportCsv} data-testid="deadlines-export-button"><Download className="h-4 w-4" /> Export CSV</Button>
          <Button variant="outline" onClick={exportPdf} data-testid="deadlines-export-pdf-button"><FileText className="h-4 w-4" /> Export PDF</Button>
        </div>
      </section>

      {error && <Alert variant="destructive" data-testid="deadlines-error"><AlertTriangle className="h-4 w-4" /><AlertTitle>Errore</AlertTitle><AlertDescription>{error}</AlertDescription></Alert>}

      <section className="grid gap-3 md:grid-cols-5" data-testid="deadlines-stats">
        <StatTile label="Totale" value={summary.total} accent="text-slate-950" testId="deadlines-stat-total" />
        <StatTile label="Scadute" value={summary.overdue} accent="text-red-700" testId="deadlines-stat-overdue" />
        <StatTile label="Urgenti (≤7gg)" value={summary.urgent} accent="text-orange-700" testId="deadlines-stat-urgent" />
        <StatTile label="A breve (≤30gg)" value={summary.soon} accent="text-amber-700" testId="deadlines-stat-soon" />
        <StatTile label="Pianificate" value={summary.scheduled} accent="text-emerald-700" testId="deadlines-stat-scheduled" />
      </section>

      <Card className="shadow-none" data-testid="deadlines-filters-card">
        <CardHeader>
          <CardTitle className="font-heading flex items-center gap-2"><Filter className="h-5 w-5 text-blue-900" /> Filtri</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid gap-3 md:grid-cols-3">
            <select className="h-9 rounded-md border border-slate-200 bg-white px-3 text-sm" value={filterSource} onChange={(e) => setFilterSource(e.target.value)} data-testid="deadlines-filter-source">
              <option value="">Tutte le fonti</option>
              {Object.entries(sourceMeta).map(([key, m]) => <option key={key} value={key}>{m.label}</option>)}
            </select>
            <select className="h-9 rounded-md border border-slate-200 bg-white px-3 text-sm" value={filterSeverity} onChange={(e) => setFilterSeverity(e.target.value)} data-testid="deadlines-filter-severity">
              <option value="">Tutte le severità</option>
              {Object.entries(severityMeta).map(([key, m]) => <option key={key} value={key}>{m.label}</option>)}
            </select>
            <Input placeholder="Cerca per titolo, azienda, descrizione..." value={search} onChange={(e) => setSearch(e.target.value)} data-testid="deadlines-filter-search" />
          </div>
        </CardContent>
      </Card>

      <Card className="shadow-none" data-testid="deadlines-list-card">
        <CardHeader>
          <CardTitle className="font-heading flex items-center gap-2"><CalendarClock className="h-5 w-5 text-blue-900" /> Elenco scadenze · {filteredItems.length}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {filteredItems.length === 0 && <p className="rounded-md border border-dashed border-slate-300 p-6 text-sm text-slate-500" data-testid="deadlines-empty">Nessuna scadenza corrispondente. Le scadenze appariranno qui automaticamente dai moduli operativi.</p>}
          {filteredItems.map((item) => {
            const SourceIcon = sourceMeta[item.source]?.icon || CalendarClock;
            return (
              <div key={item.id} className="flex flex-col gap-3 rounded-md border border-slate-200 p-4 sm:flex-row sm:items-center sm:justify-between" data-testid={`deadline-row-${item.id}`}>
                <div className="flex items-start gap-3">
                  <SourceIcon className="mt-1 h-5 w-5 text-blue-900" />
                  <div>
                    <p className="font-semibold text-slate-950" data-testid={`deadline-title-${item.id}`}>{item.title}</p>
                    <p className="text-sm text-slate-500" data-testid={`deadline-meta-${item.id}`}>{item.company_name} · scadenza {item.due_date}</p>
                    {item.description && <p className="mt-1 text-sm text-slate-600">{item.description}</p>}
                  </div>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <Badge className={sourceMeta[item.source]?.color || "bg-slate-100 text-slate-700"} data-testid={`deadline-source-${item.id}`}>{sourceMeta[item.source]?.label || item.source}</Badge>
                  <Badge className={`border ${severityMeta[item.severity]?.color || "bg-slate-50 text-slate-700"}`} data-testid={`deadline-severity-${item.id}`}>{severityMeta[item.severity]?.label || item.severity}</Badge>
                  <Badge className="bg-slate-50 text-slate-700" data-testid={`deadline-days-${item.id}`}>{item.is_overdue ? `Scaduta da ${Math.abs(item.days_remaining)} gg` : `Tra ${item.days_remaining} gg`}</Badge>
                </div>
              </div>
            );
          })}
        </CardContent>
      </Card>
    </div>
  );
}
